'use client';

import { FC, ReactNode } from 'react';

import { Button, CtaLink, HeroBanner, Link } from '@purpur/library';

import { PlainLink } from '@telialt/common-plain-link/src';
import { InlineRichText } from '@telialt/common-rich-text-content/src/InlineRichText';
import { RichTextContent } from '@telialt/common-rich-text-content/src/RichTextContent';

import { ImageLink } from '../common/ImageLink';
import { ResponsiveImage } from '../common/ResponsiveImage';
import { CtaProvider } from '../CtaProvider/CtaProvider';
import { StyledHeroBanner } from './BlockHeroBanner.styles';
import { HeroBannerVideo } from './HeroBannerVideo';
import { HeroBannerContent } from './model';

type Props = {
    content: HeroBannerContent;
    headingTag: 'h1' | 'h2';
    itemIndex: number;
    breadcrumbs?: ReactNode;
};
export const BlockHeroBannerContent: FC<Props> = ({ content, headingTag, itemIndex, breadcrumbs }) => {
    const { title, description, preamble, legalText, primaryCta, secondaryCta, tertiaryCta, video, images } = content;

    const imageComponent = images && (
        <HeroBanner.Media>
            <ResponsiveImage images={images} fetchPriority={itemIndex === 0 ? 'high' : undefined} />
        </HeroBanner.Media>
    );
    const videoComponent = video && <HeroBannerVideo {...video} />;

    const media = videoComponent || imageComponent;
    const hasCtas = !!primaryCta || !!secondaryCta || !!tertiaryCta;

    const heroBannerContent =
        !!description || hasCtas ? (
            <>
                {!!description && <RichTextContent richText={description} isNegative={true} />}
                {hasCtas && (
                    <HeroBanner.Actions>
                        {primaryCta &&
                            (primaryCta.image ? (
                                <ImageLink cta={primaryCta} />
                            ) : (
                                <CtaProvider cta={primaryCta}>
                                    {({ href, target, rel, text, onClick }) =>
                                        href ? (
                                            <CtaLink
                                                negative={true}
                                                variant="primary"
                                                href={href}
                                                target={target}
                                                rel={rel}
                                                onClick={onClick}
                                                data-element="Button"
                                                data-element-name={text}
                                                data-element-type="Primary"
                                            >
                                                {text}
                                            </CtaLink>
                                        ) : (
                                            <Button
                                                negative={true}
                                                variant="primary"
                                                onClick={onClick}
                                                data-element="Button"
                                                data-element-name={text}
                                                data-element-type="Primary"
                                            >
                                                {text}
                                            </Button>
                                        )
                                    }
                                </CtaProvider>
                            ))}
                        {secondaryCta &&
                            (secondaryCta.image ? (
                                <ImageLink cta={secondaryCta} />
                            ) : (
                                <CtaProvider cta={secondaryCta}>
                                    {({ href, target, rel, text, onClick }) =>
                                        href ? (
                                            <CtaLink
                                                negative={true}
                                                variant="secondary"
                                                href={href}
                                                target={target}
                                                rel={rel}
                                                onClick={onClick}
                                                data-element="Button"
                                                data-element-name={text}
                                                data-element-type="Secondary"
                                            >
                                                {text}
                                            </CtaLink>
                                        ) : (
                                            <Button
                                                negative={true}
                                                variant="secondary"
                                                onClick={onClick}
                                                data-element="Button"
                                                data-element-name={text}
                                                data-element-type="Secondary"
                                            >
                                                {text}
                                            </Button>
                                        )
                                    }
                                </CtaProvider>
                            ))}
                        {tertiaryCta &&
                            (tertiaryCta.image ? (
                                <ImageLink cta={tertiaryCta} />
                            ) : (
                                <CtaProvider cta={tertiaryCta}>
                                    {({ href, target, rel, text, onClick }) =>
                                        href ? (
                                            <Link
                                                negative={true}
                                                variant="standalone"
                                                href={href}
                                                target={target}
                                                rel={rel}
                                                onClick={onClick}
                                                data-element="Button"
                                                data-element-name={text}
                                                data-element-type="Tertiary"
                                            >
                                                {text}
                                            </Link>
                                        ) : (
                                            <PlainLink
                                                onClick={onClick}
                                                negative={true}
                                                styledAsLink={true}
                                                withPadding={true}
                                                size="sm"
                                                data-element="Button"
                                                data-element-name={text}
                                                data-element-type="Tertiary"
                                            >
                                                {text}
                                            </PlainLink>
                                        )
                                    }
                                </CtaProvider>
                            ))}
                    </HeroBanner.Actions>
                )}
            </>
        ) : undefined;

    return (
        <StyledHeroBanner
            title={title}
            headingTag={headingTag}
            preamble={preamble}
            additionalText={<InlineRichText richText={legalText} isNegative={true} />}
            media={media}
            breadcrumbs={breadcrumbs}
            data-section="Carousel Card"
            data-section-name={title}
            data-section-index={itemIndex + 1}
        >
            {heroBannerContent}
        </StyledHeroBanner>
    );
};
